// File: client_serena/src/components/UserMealPlan.js

import React, { useEffect, useState } from "react";
import { Box, Heading, Text, SimpleGrid, VStack, useDisclosure, useToast } from "@chakra-ui/react";
import MealCard from "./MealCard";
import MealInfoModal from "./MealInfoModal";
import { fetchPurchasedMealIds } from "../api/fetchPurchasedMeals";

import { useAuth } from "../hooks/useAuth";
import { replaceMeal } from "../api/replaceMeal";


const categoryHeadings = {
  breakfast: {
    title: "🍳 Morning Grub (Breakfast)",
    nudge: "Something light for the road. No dey skip breakfast o!",
  },
  lunch: {
    title: "🥘 Midday Chop (Lunch)",
    nudge: "Balanced chow wey dey hold belle.",
  },
  dinner: {
    title: "🌙 Evening Enjoyment (Dinner)",
    nudge: "Light, clean chow wey dey calm body.",
  },
  snack: {
    title: "🍌 Anytime Munch (Snacks)",
    nudge: "Snack wey dey inspire. No junk here.",
  },
};


function UserMealPlan({
  mealPlan = {},
  purchasedMeals = [],
  favoriteMeals = [],
  setPurchasedMeals,
  userPreferences = {},
  healthMetrics = {},
}) {

  const [groupedMeals, setGroupedMeals] = useState({});
  const [replaceCounts, setReplaceCounts] = useState({}); // { mealId: count }
  const [selectedMeal, setSelectedMeal] = useState(null);
  const infoModal = useDisclosure();
  const toast = useToast();
  const { role, user } = useAuth();
  const isSubscribed = user?.subscriptionStatus === "subscribed" || role === "subscriber";

  useEffect(() => {
  if (!mealPlan || typeof mealPlan !== "object") return;

  console.log("🔍 First breakfast meal sample:", mealPlan.breakfast?.[0]);

  // Validate structure
  Object.entries(mealPlan).forEach(([cat, meals]) => {
    console.log(`📁 ${cat} count:`, Array.isArray(meals) ? meals.length : "❌ not an array");

    if (Array.isArray(meals) && meals.length > 0) {
      console.log(`🔍 Sample ${cat} meal:`, meals[0]);
    }
  });
}, [mealPlan]);

  useEffect(() => {
    if (!mealPlan || typeof mealPlan !== "object") return;

    console.log("📦 Raw mealPlan from backend:", mealPlan);
    const newGrouped = mealPlan;

    console.log("🔍 First breakfast meal sample:", mealPlan.breakfast?.[0]);

    setGroupedMeals((prev) => {
      const same = JSON.stringify(prev) === JSON.stringify(newGrouped);
      if (!same) {
        console.log("✅ Grouped meals updated:", {
          breakfast: newGrouped.breakfast?.length || 0,
          lunch: newGrouped.lunch?.length || 0,
          dinner: newGrouped.dinner?.length || 0,
          snack: newGrouped.snack?.length || 0,
        });
        return newGrouped;
      }
      return prev;
    });
  }, [mealPlan]);

  useEffect(() => {
    if (!groupedMeals || Object.keys(groupedMeals).length === 0) return;

    console.log(
      "📦 Grouped meals breakdown:",
      Object.fromEntries(
        Object.entries(groupedMeals).map(([cat, meals]) => [
          cat,
          Array.isArray(meals) ? meals.length : 0,
        ])
      )
    );
  }, [groupedMeals]);

  useEffect(() => {
  const loadPurchasedMeals = async () => {
    try {
      const ids = await fetchPurchasedMealIds();
      setPurchasedMeals(ids);
      console.log("✅ Purchased meals loaded:", ids);
    } catch (err) {
      console.error("❌ Failed to load purchased meals:", err);
    }
  };

  if (typeof setPurchasedMeals === "function") {
    loadPurchasedMeals();
  }
}, [setPurchasedMeals]);

  if (
    !groupedMeals ||
    Object.values(groupedMeals).every((list) => !Array.isArray(list) || list.length === 0)
  ) {
    return (
      <Text mt={4} color="gray.500" fontStyle="italic">
        No meals found. Please update your preferences to get a personalized plan.
      </Text>
    );
  }


  // Smart shuffle: call backend to get a replacement meal
  const handleReplace = async (mealId, currentMeal) => {
    const category = (currentMeal.category || "lunch").toLowerCase();
    if (!isSubscribed && (replaceCounts[mealId] || 0) >= 1) {
      toast({
        title: "Replace limit reached",
        description: "Upgrade to a subscription for unlimited replacements.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const newMeal = await replaceMeal(mealId, category);
      const updated = { ...groupedMeals };
      updated[category] = updated[category].map((meal) =>
        meal._id === mealId ? newMeal : meal
      );
      setGroupedMeals(updated);
      setReplaceCounts((prev) => ({
        ...prev,
        [mealId]: (prev[mealId] || 0) + 1,
      }));
      toast({
        title: "Meal replaced",
        description: isSubscribed
          ? "Meal replaced! (Unlimited for subscribers)"
          : "Meal replaced! (One-time for non-subscribers)",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "No alternative found",
        description: err.message || "No other meals available for replacement in this category.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePurchase = (mealId, updatedList) => {
    if (setPurchasedMeals) {
      setPurchasedMeals(updatedList);
    }
  };


  // --- Precision Nutrition Filtering ---
  function isMealPerfectMatch(meal) {
    // Example: match macros, allergies, preferences, etc. (customize as needed)
    if (!meal) return false;
    // Allergies
    if (userPreferences.allergies && meal.ingredients) {
      const hasAllergy = userPreferences.allergies.some((allergy) =>
        meal.ingredients.some((ing) =>
          ing.toLowerCase().includes(allergy.toLowerCase())
        )
      );
      if (hasAllergy) return false;
    }
    // Preferences (e.g., vegan, low-carb)
    if (userPreferences.diet && meal.tags) {
      if (!meal.tags.map((t) => t.toLowerCase()).includes(userPreferences.diet.toLowerCase())) {
        return false;
      }
    }
    // Macro match (example: within 10% of target)
    if (healthMetrics.macros && meal.macros) {
      const { carbs, protein, fat } = meal.macros;
      const { carbs: tc, protein: tp, fat: tf } = healthMetrics.macros;
      const close = (a, b) => Math.abs(a - b) / (b || 1) < 0.12;
      if (!close(carbs, tc) || !close(protein, tp) || !close(fat, tf)) return false;
    }
    return true;
  }

  // Filter meals for precision nutrition (optional: show all, but highlight perfect matches)
  function filterMeals(meals) {
    if (!Array.isArray(meals)) return [];
    // Optionally, filter out meals that don't match allergies
    return meals.filter((meal) => {
      if (userPreferences.allergies && meal.ingredients) {
        const hasAllergy = userPreferences.allergies.some((allergy) =>
          meal.ingredients.some((ing) =>
            ing.toLowerCase().includes(allergy.toLowerCase())
          )
        );
        if (hasAllergy) return false;
      }
      return true;
    });
  }

  return (
    <>
      <VStack spacing={14} mt={4} align="stretch">
        {Object.entries(groupedMeals).map(([category, meals]) => {
          if (!Array.isArray(meals) || meals.length === 0) return null;
          const info = categoryHeadings[category] || {
            title: category.toUpperCase(),
            nudge: "",
          };
          const filteredMeals = filterMeals(meals);
          return (
            <Box key={category}>
              <Heading size="md" mb={1}>{info.title}</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>{info.nudge}</Text>
              <SimpleGrid columns={[1, 2, 3]} spacing={10}>
                {filteredMeals.map((meal, i) => (
                  <MealCard
                    key={meal._id || `${category}-${i}`}
                    meal={meal}
                    onReplace={handleReplace}
                    onPurchase={handlePurchase}
                    purchasedMeals={purchasedMeals}
                    favoriteMeals={favoriteMeals}
                    isPerfectMatch={isMealPerfectMatch(meal)}
                    replaceCount={replaceCounts[meal._id] || 0}
                    maxReplace={1}
                    onOpenInfo={(m) => {
                      setSelectedMeal(m);
                      infoModal.onOpen();
                    }}
                  />
                ))}
              </SimpleGrid>
            </Box>
          );
        })}
      </VStack>
      <MealInfoModal
        meal={selectedMeal}
        isOpen={infoModal.isOpen}
        onClose={infoModal.onClose}
      />
    </>
  );
}

export default UserMealPlan;

